import { TransferredBadge } from '@/components/candidate-job-slider-v1/components/common/TransferredBadge';
import { UpdateReviewStatus } from '@/components/reviews/UpdateReviewStatus';
import { AtsStages, FlowStage } from '@/components/shared/flow-stage/Stage';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { fetchCandidateById } from '@/store/contacts/actions';
import { selectCandidateById } from '@/store/contacts/selectors';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { Box, Center, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { MinusCircle } from '@kula-ai/icons-core';
import { jobStatusConfig } from '@/constants/job-constants';
import { FiInfo, FiPause } from 'react-icons/fi';
import dayjs from 'dayjs';
import { useGetATSRejectionListQuery } from '@/store/query/ats';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDateFormat } from '@/hooks/useDateFormat';

export const useGetJobsConfig = () => {
  const { dateFormat } = useDateFormat();
  const dateFmt = dateFormat?.label || 'MMM DD';
  const candidate = useSelector(selectCandidateById);
  const transfer_info = candidate?.transfer_info;

  return [
    {
      headerText: 'Name',
      width: 250,
      Cell: ({ item }) => {
        const { ats_job_name, job_id, id, pause_info } = item || {};
        const windowWidth = window.innerWidth;
        const maxW = windowWidth > 1280 ? '350px' : '500px';

        const appTransferInfo = transfer_info?.applications?.[String(id)];
        const hasTransferredIn = !!appTransferInfo?.transferred_in;
        const fromApplicationId =
          appTransferInfo?.transferred_in?.from_application_id;
        const transferInfoForBadge =
          hasTransferredIn && transfer_info
            ? {
                applications: transfer_info.applications || {},
                history: fromApplicationId ? [fromApplicationId] : [],
              }
            : null;

        return (
          <Flex alignItems="center" gap="12px" maxW="100%">
            <Link to={`/jobs/${job_id}?tab=candidates&appId=${id}`}>
              <TruncatedTextWithTooltip
                size="sm-semi-bold"
                _hover={{ color: 'primary-600', textDecor: 'underline' }}
                text={ats_job_name}
                maxW={maxW}
              />
            </Link>
            {transferInfoForBadge && (
              <TransferredBadge
                transferInfo={transferInfoForBadge}
                isIconOnly
              />
            )}
            {/* <Flex
              gap="0.5em"
              alignItems="center"
              border="1px solid"
              borderRadius="6px"
              p="0.125em 0.5em"
              borderColor="border-color"
            >
              <Box w="8px" h="8px" bgColor={color} borderRadius="10px"></Box>
              <KText color="ats-text-primary" size="sm-medium">
                {label}
              </KText>
            </Flex> */}
            {pause_info && (
              <Flex
                bg="ats-bg-primary"
                alignItems="center"
                justifyContent="space-between"
                p="6px"
                borderRadius="sm"
              >
                <Flex alignItems="center" gap="12px">
                  <Icon as={FiPause} w="16px" h="16px" />
                  <KText size="sm-medium">
                    Paused until{' '}
                    <b>{dayjs(pause_info?.paused_end_at)?.format(dateFmt)}</b>
                  </KText>
                  <Tooltip variant="default" label={pause_info?.reason}>
                    <Center ml="-6px">
                      <Icon as={FiInfo} w="16px" h="16px" />
                    </Center>
                  </Tooltip>
                </Flex>
              </Flex>
            )}
          </Flex>
        );
      },
    },
    {
      headerText: 'Job status',
      width: 100,
      Cell: ({ item }) => {
        const { ats_job_status } = item;
        const { color, label } = jobStatusConfig[ats_job_status] || {};
        return (
          <Flex
            alignItems="center"
            border="1px solid"
            borderRadius="6px"
            p="2px 6px"
            borderColor="border-color"
            gap="6px"
            w="fit-content"
          >
            <Box w="8px" h="8px" bgColor={color} borderRadius="10px"></Box>
            <KText color="ats-text-primary" size="sm-medium">
              {label}
            </KText>
          </Flex>
        );
      },
    },
    {
      headerText: 'Application stage',
      width: 150,
      Cell: ({ item }) => {
        const { current_ats_stage_name, status, ats_rejection_reason } = item;
        return status === 'rejected' ? (
          <Flex gap="6px" alignItems="center">
            <MinusCircle
              size="1em"
              color="var(--kula-colors-ats-negative)"
              className="kula-icon"
            />
            <KText size="sm-medium">
              {ats_rejection_reason?.reason === 'Below AI score threshold'
                ? 'System rejected'
                : 'User rejected'}{' '}
              ({ats_rejection_reason?.reason})
            </KText>
          </Flex>
        ) : (
          <KTag
            size="sm-medium"
            p="2px 8px"
            borderRadius="6px"
            width="fit-content"
            value={current_ats_stage_name}
          ></KTag>
        );
      },
    },
  ];
};

export const useGetProjectsConfig = ({ canEditStatus = true }) => {
  const candidate = useSelector(selectCandidateById);
  const { first_name, last_name } = candidate;
  const currUser = useSelector(selectCurrUser);
  const contactName = `${first_name + ' ' + last_name}`;
  const dispatch = useDispatch();

  const onRefetch = () => {
    if (candidate?.id) {
      dispatch(fetchCandidateById({ id: candidate?.id }));
    }
  };

  return [
    {
      headerText: 'Name',
      width: 250,
      Cell: ({ item }) => {
        const { name, id } = item;

        return (
          <Link to={`/projects/${id}`}>
            <TruncatedTextWithTooltip
              size="sm-semi-bold"
              _hover={{ color: 'primary-600', textDecor: 'underline' }}
              text={name}
              maxW="500px"
            />
          </Link>
        );
      },
    },
    {
      headerText: 'Status',
      width: 250,
      Cell: ({ item }) => {
        const {
          review_id,
          review_status,
          user_access,
          reviewer_user_id,
          status,
        } = item;

        const isUpdateAllowed =
          (['project_owner'].includes(user_access) ||
            reviewer_user_id === currUser?.id) &&
          status !== 'archived';
        const isCreateAllowed =
          ['project_editor'].includes(user_access) && status !== 'archived';
        return (
          <UpdateReviewStatus
            status={review_status}
            reviewId={review_id}
            // prospectId={prospect_id}
            isUpdateAllowed={isUpdateAllowed && canEditStatus}
            isCreateAllowed={isCreateAllowed}
            contact={{ name: contactName }}
            onRefetch={onRefetch}
            isFromAts={true}
          />
        );
      },
    },
  ];
};

export const useGetFlowsConfig = ({ canEditStage }) => {
  const candidate = useSelector(selectCandidateById);
  const dispatch = useDispatch();
  const { ats_enabled } = useKFlags();

  const { data, isFetching } = useGetATSRejectionListQuery(
    {},
    { skip: !ats_enabled }
  );

  const onStageUpdate = () => {
    if (candidate?.id) {
      dispatch(fetchCandidateById({ id: candidate?.id }));
    }
  };

  return [
    {
      headerText: 'Name',
      width: 250,
      Cell: ({ item }) => {
        const { title, id } = item;
        return (
          <Link to={`/flows/${id}/candidates`}>
            <TruncatedTextWithTooltip
              size="sm-semi-bold"
              _hover={{ color: 'primary-600', textDecor: 'underline' }}
              text={title}
              maxW="500px"
            />
          </Link>
        );
      },
    },
    {
      headerText: 'Flow stage',
      width: 250,
      Cell: ({ item }) => {
        return (
          <FlowStage
            contact={candidate}
            currStage={item?.stage}
            outreach={item}
            onStageUpdate={onStageUpdate}
            showRestart={false}
            isFromAts={true}
            rejectionList={{ data, isLoading: isFetching }}
            canEditStage={canEditStage}
          />
        );
      },
    },
    {
      headerText: ats_enabled ? 'Job stage' : 'Ats stage',
      width: 250,
      Cell: ({ item }) => {
        return (
          <AtsStages
            contact={{
              ...item,
              id: candidate?.id,
              first_name: candidate?.first_name,
              outreach_candidate_stage: item?.stage,
            }}
            outreach={item}
            onStageUpdate={onStageUpdate}
            isFromAts={true}
          />
        );
      },
    },
  ];
};
